import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, UserCog, LogOut, ExternalLink, Menu, X, Headset, GraduationCap, FileSignature, BookOpen, ClipboardCheck, BarChart3, BellRing, CalendarDays } from "lucide-react";
import { useState } from "react";

const ADMIN = ["primary", "admin"];
const ALL = ["primary", "admin", "trainer", "mentor"];
const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ADMIN },
  { to: "/admin/applicants", label: "Applicants", icon: Users, roles: ADMIN },
  { to: "/admin/students", label: "Students", icon: GraduationCap, roles: ALL },
  { to: "/admin/library", label: "Document Library", icon: FileSignature, roles: ALL },
  { to: "/admin/materials", label: "Course Materials", icon: BookOpen, roles: ALL },
  { to: "/admin/quizzes", label: "Quizzes", icon: ClipboardCheck, roles: ["primary", "admin", "trainer"] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: ADMIN },
  { to: "/admin/trainers", label: "Trainers", icon: Headset, roles: ADMIN },
  { to: "/admin/trainer-calendar", label: "Trainer Calendar", icon: CalendarDays, roles: ADMIN },
  { to: "/admin/my-availability", label: "My Availability", icon: CalendarDays, roles: ["trainer"] },
  { to: "/admin/portal-settings", label: "Notifications", icon: BellRing, roles: ADMIN },
  { to: "/admin/administrators", label: "Administrators", icon: UserCog, roles: ADMIN },
];
const ROLE_LABEL = { primary: "Primary Admin", admin: "Administrator", trainer: "Certified Trainer", mentor: "Mentor" };

export const AdminLayout = ({ children, title }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const Sidebar = (
    <aside className="bg-navy text-white w-64 flex flex-col min-h-screen fixed lg:sticky top-0 z-40" data-testid="admin-sidebar">
      <div className="p-5 border-b border-white/10">
        <span className="bg-white rounded-md px-3 py-2 inline-flex">
          <Logo className="h-10" />
        </span>
        <p className="mt-3 text-xs text-white/50 uppercase tracking-widest">Admin Dashboard</p>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {nav.filter((n) => n.roles.includes(admin?.role)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-3 text-sm transition-colors ${isActive ? "bg-white/10 text-magenta border-l-4 border-magenta font-bold" : "text-white/75 hover:bg-white/5 border-l-4 border-transparent"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
        <a href="/" className="flex items-center gap-3 px-5 py-3 text-sm text-white/75 hover:bg-white/5 border-l-4 border-transparent">
          <ExternalLink size={18} /> Public Site
        </a>
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-xs text-white/60 mb-2 truncate" data-testid="admin-current-user">{admin?.email}</div>
        <button onClick={doLogout} data-testid="admin-logout"
          className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold py-2 rounded-md transition-colors">
          <LogOut size={16} /> Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-soft-gray flex">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && <div className="lg:hidden">{Sidebar}<div className="fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} /></div>}

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-navy/10 sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8 h-16">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-navy" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
            <h1 className="font-display text-lg font-bold text-navy" data-testid="admin-page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {admin?.role && <span className="bg-magenta-light text-magenta text-xs font-bold px-3 py-1 rounded-full" data-testid="admin-role-badge">{ROLE_LABEL[admin.role]}</span>}
            <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center font-bold text-sm">
              {(admin?.name || admin?.email || "A")[0].toUpperCase()}
            </div>
          </div>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
